import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { CalendarSetting } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { DUE_SOURCES, chipSources } from "../termine/dueItems.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { CALENDAR_TEXT } from "./calendarTexts.js";
import type { CalendarSettingsClient } from "./calendarSettingsClient.js";
import "./EinstellungenKalender.css";

const T = CALENDAR_TEXT.settings;

type Source = CalendarSetting["sources"][number];
/** Die Felder, die Speichern/Abbrechen betreffen; Abonnement und Version gehören dem Stand. */
type Draft = Pick<CalendarSetting, "layout" | "slotMinutes" | "slotStart" | "sources" | "moveBehavior">;

/** Quellen immer in der Folge der Quellentabelle (D10): gleicher Stand ergibt gleichen Vergleich und gleiche Sendung. */
const sortSources = (l: readonly Source[]): Source[] => [...l].sort((a, b) => DUE_SOURCES.indexOf(a) - DUE_SOURCES.indexOf(b));
const SLOT_MINUTES = [15, 30, 45, 60, 90, 120] as const;
/** Felder mit eigenem Eingabefeld — ein 422 an einem anderen Feld wird allgemein gemeldet. */
const KNOWN_FIELDS = new Set(["slotStart", "slotMinutes"]);

const draftOf = (s: CalendarSetting): Draft => ({ layout: s.layout, slotMinutes: s.slotMinutes,
  slotStart: s.slotStart, sources: sortSources(s.sources), moveBehavior: s.moveBehavior });
const sameDraft = (a: Draft, b: Draft): boolean => JSON.stringify(a) === JSON.stringify(b);

function dayText(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" });
}

async function clipboardCopy(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

/**
 * EINSTELLUNGEN › KALENDER (Terminebene D27): Abonnement (Adresse, einrichten, erneuern, abschalten),
 * Inhalt, Darstellung, Verschieben. Inhalt, Darstellung und Verschieben sind EIN Stand mit Version am Server
 * (Speichern ist die Bestätigung, 409 lädt neu); das Abonnement schreibt sofort, aber erst nach einer gestapelten
 * Rückfrage. Die Adresse ist ein Geheimnis: sie steht nur im Arbeitsspeicher dieser Ansicht, nie im Browserspeicher.
 */
export function EinstellungenKalender(p: { client: CalendarSettingsClient; supplierView: boolean;
  host?: string; kopieren?: (text: string) => Promise<void> }): ReactElement {
  const host = p.host ?? location.host;
  const copy = p.kopieren ?? clipboardCopy;
  const { bestaetigen } = useBestaetigung();
  const [setting, setSetting] = useState<CalendarSetting | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Welche Adresszeile zuletzt kopiert wurde (true) oder scheiterte (false). */
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  function take(s: CalendarSetting, keepDraft: boolean): void {
    setSetting(s);
    if (!keepDraft) setDraft(draftOf(s));
  }

  useEffect(() => {
    let active = true;
    void p.client.getCalendarSetting().then((s) => { if (active) take(s, false); })
      .catch(() => { if (active) setLoadFailed(true); });
    return () => { active = false; };
  }, [p.client]);

  if (setting === null || draft === null) {
    return loadFailed ? <Hinweis art="fehler">{T.loadFailed}</Hinweis> : <p>{T.loading}</p>;
  }
  const current = setting;
  const edit = draft;
  const dirty = !sameDraft(edit, draftOf(current));
  const offered: Source[] = chipSources("CALENDAR", p.supplierView).filter((x): x is Source => x !== "TICKET");

  function change(part: Partial<Draft>): void {
    setDraft({ ...edit, ...part, ...(part.sources === undefined ? {} : { sources: sortSources(part.sources) }) });
  }
  function slotStart(value: string): void {
    setFieldErrors(({ slotStart: _weg, ...rest }) => rest);
    change({ slotStart: value });
  }

  async function save(): Promise<void> {
    setBusy(true); setFieldErrors({}); setMessage(null);
    try { take(await p.client.updateCalendarSetting(current.version, edit), false); }
    catch (f) {
      if (f instanceof ApiError && f.status === 422) {
        const fields = f.fields ?? [];
        setFieldErrors(Object.fromEntries(fields.filter((x) => KNOWN_FIELDS.has(x.field)).map((x) => [x.field, x.message])));
        if (fields.length === 0 || fields.some((x) => !KNOWN_FIELDS.has(x.field))) setMessage(T.invalid);
      } else if (f instanceof ApiError && f.status === 409) {
        setMessage(T.changedElsewhere);
        try { take(await p.client.getCalendarSetting(), false); } catch { /* bleibt beim alten Stand */ }
      } else setMessage(T.saveFailed);
    } finally { setBusy(false); }
  }
  function cancel(): void {
    setDraft(draftOf(current)); setFieldErrors({}); setMessage(null);
  }

  /** Abonnement schreibt sofort, nach der Rückfrage; der Entwurf bleibt, die Version rückt vor. */
  async function subscription(question: Parameters<typeof bestaetigen>[0],
    write: (version: number) => Promise<CalendarSetting>): Promise<void> {
    if (busy || !(await bestaetigen(question))) return;
    setBusy(true); setMessage(null); setCopied({});
    try { take(await write(current.version), true); }
    catch (f) {
      if (f instanceof ApiError && f.status === 409) {
        setMessage(T.changedElsewhere);
        try { take(await p.client.getCalendarSetting(), true); } catch { /* bleibt beim alten Stand */ }
      } else if (f instanceof ApiError && f.status === 503) {
        setMessage(T.unavailable);
        try { take(await p.client.getCalendarSetting(), true); } catch { /* bleibt beim alten Stand */ }
      } else setMessage(T.writeFailed);
    } finally { setBusy(false); }
  }
  async function copyUrl(key: string, url: string): Promise<void> {
    try { await copy(url); setCopied({ [key]: true }); }
    catch { setCopied({ [key]: false }); }
  }

  const sub = current.subscription;
  const rows = sub === null ? [] : [
    { key: "personal", label: T.personal, path: sub.path },
    ...sub.teams.map((t) => ({ key: `team:${t.id}`, label: T.teamRow(t.name), path: t.path })),
  ];

  return (
    <>
      <fieldset className="es__gruppe ek__section">
        <legend>{T.subscription}</legend>
        {!current.available ? <Hinweis art="info">{T.unavailable}</Hinweis> : (
          <>
            <p>{sub === null ? T.notSetUp : T.setUpSince(dayText(sub.since))}</p>
            <Hinweis art="warnung">{T.secretNotice}</Hinweis>
            {rows.map((r) => {
              const url = `https://${host}${r.path}`;
              return (
                <div className="ek__address" key={r.key}>
                  <span className="ek__label">{r.label}</span>
                  <span className="ek__url">{url}</span>
                  <div className="ek__actions">
                    <KiKnopf name={T.copyName(r.label)} onClick={() => { void copyUrl(r.key, url); }}>{T.copy}</KiKnopf>
                    <a className="ek__link" aria-label={T.openName(r.label)} href={`webcal://${host}${r.path}`}>{T.open}</a>
                  </div>
                  {copied[r.key] === true && <Hinweis art="ok">{T.copied}</Hinweis>}
                  {copied[r.key] === false && <Hinweis art="warnung">{T.copyFailed}</Hinweis>}
                </div>
              );
            })}
            <div className="ek__actions">
              {sub === null
                ? <KiKnopf art="primaer" deaktiviert={busy} onClick={() => {
                  void subscription({ titel: T.confirmCreateTitle, text: T.confirmCreate, bestaetigenText: T.create },
                    (v) => p.client.rotateCalendarFeedToken(v));
                }}>{T.create}</KiKnopf>
                : <>
                  <KiKnopf deaktiviert={busy} onClick={() => {
                    void subscription({ titel: T.confirmRotateTitle, text: T.confirmRotate, bestaetigenText: T.rotate },
                      (v) => p.client.rotateCalendarFeedToken(v));
                  }}>{T.rotate}</KiKnopf>
                  <KiKnopf art="gefahr" deaktiviert={busy} onClick={() => {
                    void subscription({ titel: T.confirmRemoveTitle, text: T.confirmRemove, bestaetigenText: T.remove,
                      gefaehrlich: true }, (v) => p.client.deleteCalendarFeedToken(v));
                  }}>{T.remove}</KiKnopf>
                </>}
            </div>
          </>
        )}
      </fieldset>

      <fieldset className="es__gruppe ek__section">
        <legend>{T.content}</legend>
        <p>{T.alwaysIncluded}</p>
        <Optionen mehrfach beschriftung={T.sources} wert={edit.sources.filter((s) => offered.includes(s))}
          onGeaendert={(d) => change({ sources: [...(d.werte ?? []) as Source[],
            ...edit.sources.filter((s) => !offered.includes(s))] })}>
          {offered.map((s) => <option key={s} value={s}>{DUE_ITEM_TEXT.chipLabel[s]}</option>)}
        </Optionen>
      </fieldset>

      <fieldset className="es__gruppe ek__section">
        <legend>{T.display}</legend>
        <Optionen beschriftung={T.layout} wert={edit.layout} onGeaendert={(d) => change({ layout: d.wert as Draft["layout"] })}>
          <option value="ALL_DAY">{T.layoutAllDay}</option>
          <option value="SLOTTED">{T.layoutSlotted}</option>
        </Optionen>
        {edit.layout === "SLOTTED" && (
          <div className="ek__slots">
            <Auswahl beschriftung={T.slotMinutes} wert={String(edit.slotMinutes)}
              {...(fieldErrors["slotMinutes"] !== undefined ? { fehler: fieldErrors["slotMinutes"] } : {})}
              onGeaendert={(d) => change({ slotMinutes: Number(d.wert) })}>
              {SLOT_MINUTES.map((n) => <option key={n} value={String(n)}>{T.slotMinutesOption(n)}</option>)}
            </Auswahl>
            <Eingabe art="uhrzeit" beschriftung={T.slotStart} wert={edit.slotStart}
              {...(fieldErrors["slotStart"] !== undefined ? { fehler: fieldErrors["slotStart"] } : {})}
              onGeaendert={(d) => slotStart(d.wert)} />
          </div>
        )}
      </fieldset>

      <fieldset className="es__gruppe ek__section">
        <legend>{T.move}</legend>
        <Optionen beschriftung={T.moveBehavior} wert={edit.moveBehavior}
          onGeaendert={(d) => change({ moveBehavior: d.wert as Draft["moveBehavior"] })}>
          <option value="RESUBMIT">{T.moveResubmit}</option>
          <option value="DUE">{T.moveDue}</option>
        </Optionen>
        <Hinweis art="info">{T.moveNotice}</Hinweis>
      </fieldset>

      {message !== null && <Hinweis art="warnung">{message}</Hinweis>}
      <div className="ek__actions">
        <KiKnopf art="primaer" deaktiviert={busy || !dirty} onClick={() => { void save(); }}>{T.save}</KiKnopf>
        <KiKnopf deaktiviert={busy || !dirty} onClick={cancel}>{T.cancel}</KiKnopf>
      </div>
    </>
  );
}
